import {Show,For, createSignal, JSX} from 'solid-js';
import {createMemo} from 'solid-js';
import {css} from '@emotion/css';
import {Dynamic} from 'solid-js/web';
import { DragDropProvider, DragDropSensors, SortableProvider, closestCenter,DragOverlay, DragEventHandler,createSortable} from '@thisbeyond/solid-dnd';
import { theme } from 'Shared/ThemeProvider';

export const repeaterColour = '#f4f4f4';

const repeaterItemStyle = () => css({
	display: 'block',
	marginBottom: 20,
	transition: 'max-height 0.3s'
});

const disabledBarStyle = () => css({
	'.fa-ellipsis-v': {
		opacity: 0.25,
		cursor: 'pointer'
	}
});

const repeaterTitleStyle = () => css(theme().root, {
	backgroundColor: 'unset',
	flex: '1 0',
	whiteSpace: 'nowrap',
	overflowX: 'hidden'
});

const buttonStyle = () => css({
	fontSize: 13,
	fontWeight: 400,
	lineHeight: '16px',
	userSelect: 'none',
	color: '#666',
	backgroundColor: 'inherit',
	border: 'unset',
	padding: '4px 9px',
	margin: '0 8px',
	cursor: 'pointer'
});

const overlayBarStyle = () => css({
	cursor: 'grab',
});

const underlayStyle = () => css({
	opacity: 0.25,
});

  
export interface IRepeaterProps<ItemData> {
	ref: HTMLElement,
	items:			ItemData[],
	setItems:		(items:ItemData[]) => void,
	deleteItem:		(e:Event,index:number) => void,
	reorder:		(fromIndex:number,toIndex:number) => void,
	renderTitle:	(current:ItemData) => string,
	renderAddLabel:	() => JSX.Element|string,
	current?:		() => number|undefined,
	setCurrent?:	(index:number|undefined) => void,
	barStyle?:		any,
	children:		(item:ItemData[],index:number) => JSX.Element
}


export function Repeater<ItemData>(P:IRepeaterProps<ItemData>)
{
	let current,setCurrent;
	if (P.current) {
		current = P.current;
		setCurrent = P.setCurrent;
	}
	else 
		[current,setCurrent] = createSignal<number|undefined>(undefined);

	/* Used for sorting */
	//XXX The DND library doesn't work for id=0. The "underlay" breaks for the first item... so start with 1.
	//	  This is a little inconvenient, otherwise we could make id=index. Lodging a defect report...
	const ids = () => [...Array(P.items.length).keys()].map(v => v+1);
	const indexed = createMemo(() => P.items.map((e:ItemData,i:number) => ({id:i+1,data:e})));

	/* Initialise dragging */
	const [activeItem, setActiveItem] = createSignal(null);

	const onDragStart:DragEventHandler = e => {
		if (P.onChange)
			P.onChange(undefined);
		setCurrent(undefined);
		setActiveItem(P.renderTitle(P.items[Number(e.draggable.id) - 1]) as any);
	};

	const onDragEnd:DragEventHandler = ({draggable,droppable})  => {
		if (!draggable || !droppable) return;

		const fromIndex = indexed().findIndex( (item:{id:number}) => item.id == draggable.id);
		const toIndex = indexed().findIndex( (item:{id:number}) => item.id == droppable.id);

		if (fromIndex === toIndex) return;

		const newItems = P.items.slice();
		newItems.splice(toIndex, 0, ...newItems.splice(fromIndex, 1));
		P.setItems(newItems);

		P.reorder(fromIndex,toIndex);
	};

	const repeaterItems = () => (
		<repeater-items ref={P.ref}> 
			<For each={indexed()}>{(item,index) => 
				<Item id={item.id} 
					current={current} 
					setCurrent={setCurrent} 
					index={index()} 
					item={item.data} 
					deleteItem={P.deleteItem}
					renderTitle={P.renderTitle}
					barStyle={P.barStyle ?? {}}
					children={P.children}
				/>
			}</For>
		</repeater-items> 
	);

	const withDragDrop = () => (
		<DragDropProvider onDragStart={onDragStart} onDragEnd={onDragEnd} collisionDetector={closestCenter} >
			<DragDropSensors />
			<SortableProvider ids={ids()}>
				{repeaterItems()}
			</SortableProvider>

			<DragOverlay>
				<repeater-item class='collapsed'>
					<repeater-bar class={overlayBarStyle()}>
						<i class='fas fa-ellipsis-v'></i> 

						<repeater-title class={repeaterTitleStyle()}>
							{activeItem()}
						</repeater-title>
					</repeater-bar>
				</repeater-item>
			</DragOverlay>
		</DragDropProvider>
	);

	return (
		<Dynamic component={current() == undefined ? withDragDrop : repeaterItems} />
	);
}

interface ItemProps<ItemData> {
	id: number,
	current: () => number|undefined,
	setCurrent: (index:number|undefined) => void,
	index: number,
	item: ItemData,
	renderTitle: (current:ItemData) => string,
	/* If deleteItem isn't present then hide the button */
	deleteItem?: (e:Event,i:number) => void,
	barStyle?: any,
	children: (item:ItemData[],index:number) => JSX.Element
}

function Item<ItemData>(P:ItemProps<ItemData>)
{
	const barStyle = () => css({
		display: 'flex',
		alignItems: 'center',
		padding: '12px 25px 12px 0',
		cursor: 'pointer',
		userSelect: 'none',
		backgroundColor: repeaterColour,
		color: '#000',
		...P.barStyle,

		'.fa-ellipsis-v': {
			cursor: 'grabbing',
			padding: '4px 15px',
			color: 'gray'    //XXX this is the current value of  @repeaterBarGrabber;
		}
	});

	/*
		Unfortunately DragDropProviders don't nest properly - parents nodes end up being dragged as well as children.
		To solve this I'm removing 'sortable' here. An alternative would be to remove DragDropProvider for the parents.
		Disabling sortability in this way means parent items have to be collapsed before sorting.
	*/
	const isSortable = P.current()==undefined;
	const sortable = isSortable ? createSortable(P.id,/*Think this is meant to any*/P.item as any) : () => {};

	const show = () => P.current()==P.index;
	const toggleShow = () => P.setCurrent(show() ? undefined : P.index);

	return (
		<repeater-item use:sortable  
			classList={{
				[repeaterItemStyle()]: true,
				expanded: show(),
				collapsed: !show(),
				[underlayStyle()]: (sortable as any).isActiveDraggable
			}}
		>
			<repeater-bar onClick={toggleShow}
				classList={{
					[barStyle()]: true,
					[disabledBarStyle()]: !isSortable
				}}
			>
				<i class='fas fa-ellipsis-v'></i> 
				<repeater-title class={repeaterTitleStyle()}>
					{ P.renderTitle(P.item) }
				</repeater-title>
				<repeater-buttons>
					<button-group>
						{show() ?
							<button class={buttonStyle()}><i class='fas fa-compress' title='Collapse'></i></button>
						:
							<button class={buttonStyle()}><i class='fas fa-expand' title='Expand'></i></button>
						}
					</button-group>
					<Show when={P.deleteItem}>
						<button class={buttonStyle()} onClick={e => P.deleteItem(e,P.index)}>
							<i class='fas fa-trash' title='Delete'></i>
						</button>
					</Show>
				</repeater-buttons>
			</repeater-bar>

			<Show when={show()}>
				{P.children(P.item, P.index) } 
			</Show>
		</repeater-item>
	);
}

